import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { email, form, FormField, required, schema } from '@angular/forms/signals';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { Observable, finalize as rxFinalize, of, map as rxMap, tap as rxTap } from 'rxjs';
import { BudgetUpdateResultDto, MceApiService, StringSelectDto, UserDto } from '../../core/mceapi.client.generated';
import { apiOptionDtoToOption } from '../../core/mceapi.client.helpers';
import { isSome } from '../../shared/functions/functional';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { hasRole, Role } from '../../shared/enums/role';
import { SupportUserStripeComponent } from './support-user-stripe.component';
import { MatCardModule } from '@angular/material/card';

interface UserManagementModel {
  searchString: string,
  newEmail: string;
};

const userManagementFormSchema = schema<UserManagementModel>((path) => {
  required(path.newEmail, { message: 'New email is required' });
  email(path.newEmail, { message: 'Enter a valid email address' });
});

@Component({
  selector: 'mce-support-user',
  imports: [CommonModule, FormField,
    MatAutocompleteModule, MatInputModule, MatProgressSpinnerModule, MatCardModule,
    MatButtonModule, MatIconModule, MatTooltipModule, ClipboardModule, RouterLink, SupportUserStripeComponent],
  templateUrl: './support-user.component.html'
})
export class SupportUserComponent {

  private mceApiService = inject(MceApiService);
  private dialogService = inject(DialogService);

  readonly userManagementModel = signal<UserManagementModel>({
    searchString: '',
    newEmail: ''
  });
  readonly form = form<UserManagementModel>(this.userManagementModel, userManagementFormSchema)


  user = signal<UserDto>({});
  processing = signal(false);

  private rolesResource = rxResource({
    params: () => ({ userUid: this.user().uid }),
    stream: ({ params }) =>
      params.userUid ? this.mceApiService.v1UsersRoles(params.userUid) : of([])
  });
  roles = computed(() => this.rolesResource.value());

  options = computed<Observable<StringSelectDto[]>>(() => {
    const searchString = this.form.searchString().value();
    return (searchString.length >= 3 && searchString !== 'bin')
      ? this.mceApiService.v1UsersSearchSearchString(searchString).pipe(
        rxMap(users => users
          .filter(user => user.user?.email)
          .map(user => ({ label: user.key, value: user.user?.email ?? '' }))))
      : of([]);

  });

  projectsRx = rxResource({
    params: () => ({ userUid: this.user().uid }),
    stream: ({ params }) =>
      params.userUid  ? this.mceApiService.v1ProjectsUser(params.userUid) : of([])
  });
  projects = computed(() => this.projectsRx.value());

  protected readonly viewState = computed(() => {
    const roles = this.roles();
    const processing = this.processing();

    return {
      user: this.user(),
      roles,
      showUser: this.user().uid !== undefined,
      showSpinner: processing,
      canResetFreeTier: roles !== undefined
        && !hasRole(roles, Role.ProTier)
        && !hasRole(roles, Role.SharedBillingMember)
        && !processing,
    };
  });


  optionSelected($event: MatAutocompleteSelectedEvent) {
    this.processing.set(true);
    this.mceApiService.v1UsersSearchEmail($event.option.value).pipe(
      rxMap(userAPIOption => apiOptionDtoToOption(userAPIOption)),
      rxTap(userOption => {
        if (isSome(userOption)) {
          this.user.set(userOption.value);
        }
        this.processing.set(false);
      })
    ).subscribe();
  }
  _setEmailField = effect(() => {
    const email = this.user().email ?? '';
    this.form.newEmail().value.set(email);
  });

  async changeEmail() {
    const user = this.user();
    const userUid = user.uid;
    const currentEmail = user.email ?? '';
    const newEmail = this.form.newEmail().value();

    if (!userUid || currentEmail === newEmail) return;

    const result = await this.dialogService.confirmAsync('Change user email', `Are you sure you want to change the email for user ${currentEmail} to ${newEmail}?`, '270px');
    if (!result) return;

    this.processing.set(true);
    this.mceApiService.v1UsersEmailUpdateAdmin({
      userUid,
      newEmail
    }).pipe(
      rxTap(user => {
        this.user.set(user);
        this.processing.set(false);
      })).subscribe();

  }

  async resetFreeTier() {
    const user = this.user();
    const userUid = user.uid;
    if (!userUid || !this.viewState().canResetFreeTier) return;

    const result = await this.dialogService.confirmAsync(
      'Reset Free Tier',
      `Are you sure you want to reset the Free Tier for user ${user.email ?? userUid}?`,
      '270px'
    );
    if (!result) return;

    this.processing.set(true);
    this.mceApiService.v1BillingFreeTierResetUser({ userUid }).pipe(
      rxTap(subscription => {
        this.user.update(user => ({ ...user, subscription }));
      }),
      rxFinalize(() => this.processing.set(false))
    ).subscribe();
  }

  viewMeterEvents() {
    const userUid = this.user().uid;
    if (!userUid) return;

    this.dialogService.meterEvents(userUid);
  }

  budgetUpdated(result: BudgetUpdateResultDto) {
    this.user.update(user => ({
      ...user,
      subscription: {
        ...user.subscription,
        paygBudget: result.paygBudget ?? user.subscription?.paygBudget,
        paygBudgetConsumed: result.paygBudgetConsumed ?? user.subscription?.paygBudgetConsumed,
      },
    }));
  }

}

